import { useState } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Users,
  Search,
  ChevronLeft,
  ChevronRight,
  Ban,
  PlayCircle,
  Mail,
  Eye,
  FileText,
  CreditCard,
  Calendar,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";
import { trpc } from "@/lib/trpc";

type StatusTab = "ALL" | "PENDING" | "VERIFIED" | "REJECTED" | "UNVERIFIED";

const STATUS_TABS: { value: StatusTab; label: string }[] = [
  { value: "ALL", label: "Tous" },
  { value: "VERIFIED", label: "Vérifiés" },
  { value: "PENDING", label: "En attente" },
  { value: "UNVERIFIED", label: "Non vérifiés" },
  { value: "REJECTED", label: "Rejetés" },
];

function statusBadge(status: string) {
  switch (status) {
    case "VERIFIED":
      return <Badge className="bg-green-100 text-green-800 border-green-200">Vérifié</Badge>;
    case "PENDING":
      return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">En attente</Badge>;
    case "REJECTED":
      return <Badge className="bg-red-100 text-red-800 border-red-200">Rejeté</Badge>;
    case "UNVERIFIED":
      return <Badge variant="outline">Non vérifié</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

export default function ArtisansAnnuaire() {
  const { user } = useAuth();
  const writable = canWrite(user?.staffRole);

  const [statusTab, setStatusTab] = useState<StatusTab>("ALL");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [department, setDepartment] = useState("");
  const [page, setPage] = useState(1);

  const [emailDialog, setEmailDialog] = useState<{ id: string; name: string } | null>(null);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");

  const [suspendDialog, setSuspendDialog] = useState<{ id: string; name: string } | null>(null);
  const [suspendReason, setSuspendReason] = useState("");

  const queryInput = {
    page,
    limit: 20,
    search: search.trim() || undefined,
    status: statusTab === "ALL" ? undefined : (statusTab as any),
    category: category || undefined,
    department: department || undefined,
  };

  const { data, isLoading } = trpc.admin.travauxbtp.listAllArtisans.useQuery(queryInput, {
    staleTime: 15_000,
  });

  const utils = trpc.useUtils();

  const suspendMutation = trpc.admin.travauxbtp.suspendArtisan.useMutation({
    onSuccess: () => {
      toast.success("Artisan suspendu");
      setSuspendDialog(null);
      setSuspendReason("");
      utils.admin.travauxbtp.listAllArtisans.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const unsuspendMutation = trpc.admin.travauxbtp.unsuspendArtisan.useMutation({
    onSuccess: () => {
      toast.success("Artisan réactivé");
      utils.admin.travauxbtp.listAllArtisans.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const emailMutation = trpc.admin.travauxbtp.sendArtisanEmail.useMutation({
    onSuccess: () => {
      toast.success("Email envoyé");
      setEmailDialog(null);
      setEmailSubject("");
      setEmailBody("");
    },
    onError: (err) => toast.error(err.message),
  });

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Users className="h-6 w-6" />
              Annuaire artisans
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              {data?.total ?? 0} artisans au total
            </p>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4 space-y-4">
            {/* Status tabs */}
            <div className="flex gap-1 flex-wrap">
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => { setStatusTab(tab.value); setPage(1); }}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                    statusTab === tab.value
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-muted hover:bg-muted/80"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Nom, email, SIRET..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  className="pl-9"
                />
              </div>
              <Select value={category} onValueChange={(v) => { setCategory(v === "_all" ? "" : v); setPage(1); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Métier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Tous les métiers</SelectItem>
                  <SelectItem value="PLOMBERIE">Plomberie</SelectItem>
                  <SelectItem value="ELECTRICITE">Electricité</SelectItem>
                  <SelectItem value="MACONNERIE">Maçonnerie</SelectItem>
                  <SelectItem value="PEINTURE">Peinture</SelectItem>
                  <SelectItem value="MENUISERIE">Menuiserie</SelectItem>
                  <SelectItem value="CARRELAGE">Carrelage</SelectItem>
                  <SelectItem value="COUVERTURE">Couverture</SelectItem>
                  <SelectItem value="CHAUFFAGE">Chauffage</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder="Département (ex: 75, 69...)"
                value={department}
                onChange={(e) => { setDepartment(e.target.value); setPage(1); }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="pt-4">
            {isLoading ? (
              <div className="py-8 text-center text-sm text-muted-foreground">Chargement...</div>
            ) : !data?.items?.length ? (
              <div className="py-8 text-center text-sm text-muted-foreground">Aucun artisan trouvé</div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Artisan</TableHead>
                      <TableHead>Métier</TableHead>
                      <TableHead>Zone</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead className="text-center">
                        <FileText className="h-3.5 w-3.5 inline" /> Docs
                      </TableHead>
                      <TableHead className="text-center">
                        <CreditCard className="h-3.5 w-3.5 inline" /> Crédits
                      </TableHead>
                      <TableHead>
                        <Calendar className="h-3.5 w-3.5 inline" /> Inscription
                      </TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.items.map((artisan: any) => (
                      <TableRow key={artisan.id}>
                        <TableCell>
                          <Link
                            to={`/travauxbtp/artisans/${artisan.id}`}
                            className="hover:underline font-medium"
                          >
                            {artisan.companyName}
                          </Link>
                          <div className="text-xs text-muted-foreground">
                            {artisan.User?.email}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {(artisan.artisanSpecialties ?? []).slice(0, 2).join(", ") || "—"}
                        </TableCell>
                        <TableCell className="text-sm">{artisan.serviceCity || "—"}</TableCell>
                        <TableCell>{statusBadge(artisan.verificationStatus)}</TableCell>
                        <TableCell className="text-center">{artisan._count?.ArtisanDocuments ?? 0}</TableCell>
                        <TableCell className="text-center font-mono">
                          {artisan.User?.Company?.leadCredits ?? 0}
                        </TableCell>
                        <TableCell className="text-xs">
                          {artisan.createdAt
                            ? new Date(artisan.createdAt).toLocaleDateString("fr-FR")
                            : "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              asChild
                              title="Voir la fiche"
                            >
                              <Link to={`/travauxbtp/artisans/${artisan.id}`}>
                                <Eye className="h-4 w-4" />
                              </Link>
                            </Button>
                            {writable && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  title="Envoyer un email"
                                  onClick={() =>
                                    setEmailDialog({ id: artisan.id, name: artisan.companyName })
                                  }
                                >
                                  <Mail className="h-4 w-4" />
                                </Button>
                                {artisan.verificationStatus === "VERIFIED" ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    title="Suspendre"
                                    onClick={() =>
                                      setSuspendDialog({ id: artisan.id, name: artisan.companyName })
                                    }
                                  >
                                    <Ban className="h-4 w-4 text-destructive" />
                                  </Button>
                                ) : artisan.verificationStatus === "REJECTED" ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    title="Réactiver"
                                    onClick={() =>
                                      unsuspendMutation.mutate({ artisanProfileId: artisan.id })
                                    }
                                  >
                                    <PlayCircle className="h-4 w-4 text-green-600" />
                                  </Button>
                                ) : null}
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {(data.totalPages ?? 1) > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-xs text-muted-foreground">
                      Page {data.page} / {data.totalPages} — {data.total} résultats
                    </p>
                    <div className="flex gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => p - 1)}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page >= (data.totalPages ?? 1)}
                        onClick={() => setPage((p) => p + 1)}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Email dialog */}
      <Dialog open={!!emailDialog} onOpenChange={(o) => { if (!o) setEmailDialog(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Envoyer un email à {emailDialog?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Objet</Label>
              <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)} rows={5} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialog(null)}>Annuler</Button>
            <Button
              disabled={!emailSubject.trim() || !emailBody.trim() || emailMutation.isPending}
              onClick={() => {
                if (!emailDialog) return;
                emailMutation.mutate({
                  artisanProfileId: emailDialog.id,
                  subject: emailSubject.trim(),
                  body: emailBody.trim(),
                });
              }}
            >
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend dialog */}
      <Dialog open={!!suspendDialog} onOpenChange={(o) => { if (!o) setSuspendDialog(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspendre {suspendDialog?.name} ?</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Raison (optionnel)</Label>
            <Textarea value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)} rows={3} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendDialog(null)}>Annuler</Button>
            <Button
              variant="destructive"
              disabled={suspendMutation.isPending}
              onClick={() => {
                if (!suspendDialog) return;
                suspendMutation.mutate({
                  artisanProfileId: suspendDialog.id,
                  reason: suspendReason.trim() || undefined,
                });
              }}
            >
              Suspendre
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
